"use client";

import { useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import {
  StatsigClient,
  StatsigEnvironment,
  StatsigProvider,
  StatsigUser,
  useClientBootstrapInit,
} from "@statsig/react-bindings";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import { getCookie } from "cookies-next";

import { useClientEnvironment } from "src/app/environment/client";
import {
  INITIAL_LOCALE_COOKIE,
  PREFERRED_LOCALE_COOKIE,
} from "~/utils/constants";

type UserData = {
  userID: string;
  custom?: {
    full_service?: boolean;
    isMobile?: boolean;
    listingCountry?: string;
    search_metro?: string;
    urgency?: boolean;
    useType?: string;
  };
  customIDs: {
    segmentAnonymousID: string;
    stableID: string;
  };
  locale?: string;
};

type StatsigValues = {
  user: UserData;
};

const getEnvironmentTier = (psEnv: string): string => {
  if (psEnv === "alpha" || psEnv === "development") return "development";
  if (psEnv === "beta") return "staging";
  return "production";
};

const StatsigBootstrapProvider = ({
  children,
  disabled,
  values,
}: {
  children: React.ReactNode;
  disabled: boolean;
  values: string;
}) => {
  const clientEnv = useClientEnvironment();
  const preferredLocale = getCookie(PREFERRED_LOCALE_COOKIE) as string;
  const initialLocale = getCookie(INITIAL_LOCALE_COOKIE) as string;
  const cookieLocale = preferredLocale || initialLocale;
  const tier = getEnvironmentTier(clientEnv.PS_ENV);
  const environment: StatsigEnvironment = { tier };

  let userData: UserData | null = null;
  try {
    const parsedValues = JSON.parse(values) as StatsigValues;
    if (parsedValues?.user) {
      userData = parsedValues.user;
    }
  } catch (e) {
    datadogRum.addError(e);
  }

  const initialUser: StatsigUser = {
    userID: userData?.userID,
    customIDs: userData?.customIDs || {
      segmentAnonymousID: "",
      stableID: "",
    },
    locale: userData?.locale || cookieLocale,
    custom: {
      search_metro: userData?.custom?.search_metro || "",
      useType: userData?.custom?.useType || "",
      urgency: userData?.custom?.urgency || false,
      isMobile: userData?.custom?.isMobile || false,
      full_service: userData?.custom?.full_service || false,
      listingCountry: userData?.custom?.listingCountry || "",
    },
  };

  // `disabled` comes from the request's user agent, so it's fixed for the
  // lifetime of the tree — switching between these never remounts.
  return disabled ? (
    <DisabledClientProvider
      environment={environment}
      initialUser={initialUser}
      sdkKey={clientEnv.STATSIG_SDK_CLIENT_KEY!}
    >
      {children}
    </DisabledClientProvider>
  ) : (
    <BootstrappedClientProvider
      environment={environment}
      initialUser={initialUser}
      sdkKey={clientEnv.STATSIG_SDK_CLIENT_KEY!}
      values={values}
    >
      {children}
    </BootstrappedClientProvider>
  );
};

type ClientProviderProps = {
  children: React.ReactNode;
  environment: StatsigEnvironment;
  initialUser: StatsigUser;
  sdkKey: string;
};

const BootstrappedClientProvider = ({
  children,
  environment,
  initialUser,
  sdkKey,
  values,
}: ClientProviderProps & { values: string }) => {
  const client = useClientBootstrapInit(sdkKey, initialUser, values, {
    environment,
    plugins: [new StatsigAutoCapturePlugin()],
  });

  return (
    <StatsigProvider client={client} options={{ environment }}>
      {children}
    </StatsigProvider>
  );
};

/**
 * Statsig client for bots: no bootstrap data, no network, no logging, no
 * plugins. Every read resolves to its default with reason `NoValues`.
 *
 * Deliberately skips `useClientBootstrapInit` — it always calls
 * `dataAdapter.setData(values)`, and the layout skips the server eval for bots,
 * so there's no valid payload to give it. Feeding it a placeholder logs
 * "Failed to parse data" + "StatsigUser not found" on every bot request.
 *
 * Still renders `StatsigProvider` so child hooks (useExperiment,
 * useFeatureGate, useStatsigUser, etc.) always have a client in context —
 * without one they throw.
 */
const DisabledClientProvider = ({
  children,
  environment,
  initialUser,
  sdkKey,
}: ClientProviderProps) => {
  const [client] = useState(() => {
    const instance = new StatsigClient(sdkKey, initialUser, {
      environment,
      loggingEnabled: "disabled",
      networkConfig: { preventAllNetworkTraffic: true },
    });
    instance.initializeSync();
    return instance;
  });

  return (
    <StatsigProvider client={client} options={{ environment }}>
      {children}
    </StatsigProvider>
  );
};

export { StatsigBootstrapProvider };
